import { Button, FormFieldLabel, Spinner, Table } from '@vwfs-bronson/bronson-react';
import React, { useState } from 'react';
import {
  IBillingError,
} from "../../../models/customer-data.interface";
import { useAppSelector } from "../../../redux/hooks";
import ErrorsModal from '../../base/errors-modal';
import InfoIconExtended from '../../base/info-icon-extended';
import './style.css';
import { GetTripPaymentResponse } from './types';
import PaymentsDataViewParams from './types/PaymentsDataViewParams';
import { selectPaymentsStore } from '../../../redux/slices/payments-slice';
import { selectNotificationStore } from '../../../redux/slices/notification-slice';
import { formatter, getFromToTimestampsPair } from '../../../utils/time-utils';
import { LabelLocales } from './locales';
import TooltipInfoIcon from '../../base/tooltip-info-icon';
import { renderBillingInvoicesList } from '../../base/billing-invoices-label';
import { TripInvoiceData } from '../../base/billing-invoices-label/data/TripInvoiceData';
import InfoIconExtendedLink from '../../base/info-icon-extended-link';
import { createDataDogQuery } from '../../../utils/DatadogUtils';

export default function PaymentsDataView({
  coursor, data, loadMoreHandler
}: PaymentsDataViewParams) {
  const paymentsStore = useAppSelector(selectPaymentsStore());
  const notificationStore = useAppSelector(selectNotificationStore());
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleOnErrorClose = () => {
    setShowErrorModal(false);
    setErrors([]);
  }

  const handleOnErrorModalShow = (errors) => {
    setErrors(errors);
    setShowErrorModal(true);
  }

  function renderBillingErrors(invoiceNumber: string, errors: IBillingError[]) {
    if (!errors?.length) {
      return null;
    }

    return (
      <ul className='row-ul'>
        {
          <li key={invoiceNumber + '_li_error'} className='row-li'>
            <span key={invoiceNumber + '_span_error'} className='row-li-inline'>
              <a onClick={() => handleOnErrorModalShow(errors)}>
                Errors ({errors.length})
              </a>
            </span>
          </li>
        }
      </ul>
    )
  }

  function renderItem(trip: GetTripPaymentResponse) {
    if (!trip) {
      return null;
    }

    const totalChargePaid = Math.round(trip.billings.reduce((total, item) => {
      if (item.state === 'COMPLETED' && item.type === 'CHARGE')
        return total += (item.totalAmount?.grossAmount || 0)

      return total;
    }, 0) * 100) / 100;

    console.log(totalChargePaid)

    const isTotalFeePaid = trip.billings.findIndex(item => item.state !== 'COMPLETED' && item.type === 'FEE') === -1;

    const tripInvoiceData: TripInvoiceData = {
      state: 'COMPLETED',
      paymentState: trip.state,
      billings: trip.billings,
      serviceProviderId: trip.serviceProviderId,
      isTotalChargePaid: (totalChargePaid || 0) === (trip.invoiceAmount?.grossAmount || 0),
      isTotalFeePaid: isTotalFeePaid,
    }

    const key = trip.referenceId;

    const hrefUri = trip.serviceProviderId === 'Europcar' ?
      `/customers/${trip.customerId}?tab=booking-history&bookingId=${trip.serviceProviderTripId}` :
      `/customers/${trip.customerId}?tab=trip-history&tripId=${trip.serviceProviderTripId}`;

    const externalId =
      trip.serviceProviderTripId?.length > 16 ? trip.serviceProviderTripId?.substring(0, 16) + '...' : trip.serviceProviderTripId;

    const { from: fromTimestamp, to: toTimestamp } = getFromToTimestampsPair();
    const dataDogUri = createDataDogQuery(trip.customerId, trip.referenceId, trip.serviceProviderTripId, fromTimestamp, toTimestamp);

    return (
      <Table.Tr key={key} className="table-row" >
        <Table.Td>{trip.customerId ?
          <><a href={`/customers/${trip.customerId}`}>{trip.customerId?.substring(0, 16) + '...'}</a>
            <InfoIconExtended label={trip.customerId} />
          </>
          :
          'guest user'
        }
        </Table.Td>
        <Table.Td>{trip.serviceProviderTripId ?
          <>
            {
              trip.customerId ? <a href={hrefUri}>{externalId}</a> : <span>{externalId}</span>
            }
            <InfoIconExtended label={trip.serviceProviderTripId} />
            <InfoIconExtendedLink
              label='See log details'
              href={dataDogUri} />
          </> :
          '-'
        }
        </Table.Td>
        <Table.Td>{trip.serviceProviderId}</Table.Td>
        <Table.Td>{trip.createdAt ? formatter.format(new Date(trip.createdAt)) : '-'}</Table.Td>
        <Table.Td>{trip.updatedAt ? formatter.format(new Date(trip.updatedAt)) : '-'}</Table.Td>
        <Table.Td>{trip?.invoiceAmount ? `${trip.invoiceAmount.grossAmount} ${trip.invoiceAmount.currency}` : '0 EUR'}</Table.Td>
        <Table.Td>{trip?.refundAmount ? `${trip.refundAmount.grossAmount} ${trip.refundAmount.currency}` : '0 EUR'}</Table.Td>
        <Table.Td>{trip.billings?.length > 0 ?
          <>
            {renderBillingInvoicesList(tripInvoiceData)}
            {renderBillingErrors(trip.referenceId, trip.errors)}
          </> : '-'}</Table.Td>
      </Table.Tr >
    );
  }

  return (
    <>
      {data?.length > 0 && (
        <Table colored={true} testId="tableComponent">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Customer ID</Table.Th>
              <Table.Th>MSP ID</Table.Th>
              <Table.Th>MSP</Table.Th>
              <Table.Th>Payment Created</Table.Th>
              <Table.Th>Payment Updated</Table.Th>
              <Table.Th>Total Invoice Value</Table.Th>
              <Table.Th>Refund Amount</Table.Th>
              <Table.Th>Payment state<TooltipInfoIcon testId="infoIconInvoicesData">Click on state for more details</TooltipInfoIcon></Table.Th>
            </Table.Tr >
          </Table.Thead >
          <Table.Tbody>{data.map((paymentData) => renderItem(paymentData))}</Table.Tbody>
        </Table >
      )
      }

      {
        data?.length === 0 && (
          <FormFieldLabel testId="noTripPaymentsLabel">
            {LabelLocales.NoPaymentFound}
          </FormFieldLabel>)
      }

      {
        data?.length > 0 && coursor && <Button onClick={loadMoreHandler}>
          {LabelLocales.LoadMore}
        </Button>
      }

      <ErrorsModal errors={[...errors]} showModal={showErrorModal} handleOnClose={handleOnErrorClose} />

      {
        paymentsStore?.status === 'failed' && (
          <FormFieldLabel testId="errorTripsLabel">{notificationStore?.error}</FormFieldLabel>)
      }

      <Spinner fullPage busy={paymentsStore.status === 'loading'} testId='tripHistorySearchSpinner' />
    </>
  );
}
